/**
 * Feature preview modal - used in two modes:
 *  - Locked: opens from the "Upgrade" pill, drives the user to billing.
 *  - Unlocked: opens from the unlocked check icon, celebrates what the user
 *    already has (no billing CTA, single "Got it" button).
 *
 * UX principle: let the video do the talking. Text is kept intentionally
 * minimal - a title, a tagline, up to 3 scan-ready bullets, and one CTA.
 *
 * @module components/ui/FeatureUpgradeModal
 */
import React from 'react';
import { Modal } from './Modal';
import { getFeatureCopy } from '../../lib/featureCatalog';
import type { FeatureKey } from '../../lib/entitlements';

/** Props for the FeatureUpgradeModal component. */
interface FeatureUpgradeModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Called when the modal should close. */
  onClose: () => void;
  /** Feature key to look up copy and assets. */
  feature: FeatureKey;
  /** Destination for the upgrade CTA. Defaults to the billing settings tab. */
  upgradeHref?: string;
  /**
   * When true, the modal renders the celebratory variant: an "Included" tag
   * and a single "Got it" button. Defaults to false (locked / upgrade flow).
   */
  unlocked?: boolean;
  /**
   * Display label for the user's current plan, used by the unlocked variant
   * (e.g. "Pro", "Business"). Falls back to the feature's required plan when
   * omitted.
   */
  currentPlanLabel?: string;
}

/** Route to the billing page. */
const DEFAULT_UPGRADE_HREF = '/admin/billing';

/**
 * Modal that previews a gated feature.
 * Renders nothing when the feature is ungated (no catalog entry) or when closed.
 */
export const FeatureUpgradeModal: React.FC<FeatureUpgradeModalProps> = ({
  open,
  onClose,
  feature,
  upgradeHref = DEFAULT_UPGRADE_HREF,
  unlocked = false,
  currentPlanLabel,
}) => {
  const copy = getFeatureCopy(feature);
  if (!copy || !open) return null;

  const planTagLabel = unlocked
    ? `${currentPlanLabel ?? copy.requiredPlan} · Included`
    : copy.requiredPlan;

  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-lg">
      <div className="space-y-5">
        {copy.videoUrl && (
          <video
            src={copy.videoUrl}
            autoPlay
            loop
            muted
            playsInline
            className="w-full aspect-video rounded-xl bg-surface object-cover"
          />
        )}

        <div className="space-y-2">
          <div className="inline-flex items-center gap-2">
            <span
              className={`px-2 py-0.5 text-xs font-semibold rounded-lg ${
                unlocked
                  ? 'bg-success/20 text-success'
                  : 'bg-volt/20 text-volt-soft'
              }`}
            >
              {planTagLabel}
            </span>
          </div>
          <h2 className="text-2xl font-bold text-foreground">{copy.title}</h2>
          <p className="text-muted-foreground">{copy.tagline}</p>
        </div>

        <ul className="space-y-2">
          {copy.bullets.map((bullet) => (
            <li key={bullet} className="flex items-start gap-2 text-muted-foreground text-sm">
              <svg
                className="w-4 h-4 mt-0.5 shrink-0 text-volt"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3 pt-2">
          {unlocked ? (
            <button
              type="button"
              onClick={onClose}
              className="flex-1 inline-flex items-center justify-center px-5 py-2.5 button-accent font-medium rounded-lg transition-colors"
            >
              Got it
            </button>
          ) : (
            <>
              <a
                href={upgradeHref}
                className="flex-1 inline-flex items-center justify-center px-5 py-2.5 button-accent font-medium rounded-lg transition-colors"
              >
                Explore {copy.requiredPlan} plan
              </a>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                Not now
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
